import { useNavigate } from 'react-router-dom'
import {
  Text,
  MagicTableCell,
  MagicTableCellAggregationType,
  MagicTableCellEmpty,
  MagicTableFilterConfig,
  useLinkMagicTableWithSearchParams,
  useMagicTableInfiniteQuery,
  usePersistMagicTableQueryParams,
  MagicTable,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  Badge,
  Button,
  SequenceMenu,
  IconCalculator,
  IconConsole,
  IconHashtag,
  IconPlus,
  IconTag,
  IconMathScientific
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { useQuery } from '@sequencehq/api'
import {
  UsageMetricFilters,
  UsageMetricsView
} from 'modules/UsageMetrics/types'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { useSetAtom } from 'jotai'
import { showCreateUsageMetricDrawerAtom } from './usageMetrics.atoms'
import { usageMetricsPaths } from 'modules/UsageMetrics/usageMetrics.router'
import {
  ViewUsageMetric,
  EditUsageMetric
} from 'modules/UsageMetrics/components/menuItems'

type UsageMetric = DashboardApi20240730.UsageMetrics.UsageMetric

const aggregationTypeOptions: { label: string; value: string }[] = [
  { label: 'Count', value: 'COUNT' },
  { label: 'Unique', value: 'UNIQUE' },
  { label: 'Sum', value: 'SUM' },
  { label: 'Custom', value: 'CUSTOM' }
]

export const UsageMetricsMagicTable = () => {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { persistMagicTableQueryParams } = usePersistMagicTableQueryParams()
  const setShowCreateUsageMetricDrawer = useSetAtom(
    showCreateUsageMetricDrawerAtom
  )

  const usageEventTypesQuery = useQuery(
    dashboard20240730Client.getUniqueUsageEventTypes
  )
  const usageEventPropertiesQuery = useQuery(
    dashboard20240730Client.getUniqueUsageEventProperties
  )
  const eventTypeOptions = (usageEventTypesQuery.data?.items ?? []).map(
    eventType => ({ label: eventType, value: eventType })
  )
  const eventPropertyOptions = (
    usageEventPropertiesQuery.data?.items ?? []
  ).map(property => ({ label: property, value: property }))

  const filters: MagicTableFilterConfig<UsageMetricFilters>[] = [
    {
      type: 'text',
      paramName: 'name',
      label: 'Name',
      icon: IconTag
    },
    {
      type: 'text',
      paramName: 'id',
      label: 'Metric ID',
      icon: IconHashtag
    },
    {
      type: 'multiSelect',
      paramName: 'eventType',
      options: eventTypeOptions,
      format: value => <Text>{value}</Text>,
      label: 'Event type',
      icon: IconConsole
    },
    {
      type: 'multiSelect',
      paramName: 'eventProperty',
      options: eventPropertyOptions,
      format: value => <Text>{value}</Text>,
      label: 'Event property',
      icon: IconMathScientific
    },
    {
      type: 'multiSelect',
      paramName: 'aggregationType',
      options: aggregationTypeOptions,
      format: value => <Text>{value}</Text>,
      label: 'Aggregation type',
      icon: IconCalculator
    }
  ]

  const { activeFilters, onChangeActiveFilters, sortBy, onChangeSortBy } =
    useLinkMagicTableWithSearchParams(filters)

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboard20240730Client.getUsageMetrics,
    filters,
    activeFilters,
    sortBy
  )
  return (
    <CurrentUserId>
      {userId => (
        <MagicTable<UsageMetricsView>
          activeFilters={activeFilters}
          onChangeActiveFilters={onChangeActiveFilters}
          sortBy={sortBy}
          onChangeSortBy={onChangeSortBy}
          filters={filters}
          entityNamePlural="usage metrics"
          infiniteQuery={infiniteQuery}
          sequenceUserId={userId}
          rowPath={row =>
            persistMagicTableQueryParams(usageMetricsPaths.view(row.id))
          }
          columns={[
            {
              id: 'id',
              header: 'Metric ID',
              accessorKey: 'id',
              cell: row => (
                <MagicTableCell
                  text={row.getValue<UsageMetric['id']>()}
                  showCopyButton={true}
                />
              )
            },
            {
              id: 'name',
              header: 'Name',
              accessorKey: 'name',
              cell: row => (
                <MagicTableCell text={row.getValue<UsageMetric['name']>()} />
              )
            },
            {
              id: 'eventType',
              header: 'Event type',
              accessorKey: 'eventType',
              cell: value => (
                <MagicTableCellEmpty>
                  <Badge sentiment="monospaced" textTransform="none" truncate>
                    {value.getValue<UsageMetric['eventType']>()}
                  </Badge>
                </MagicTableCellEmpty>
              )
            },
            {
              id: 'aggregationType',
              header: 'Aggregation type',
              accessorKey: 'aggregationType',
              cell: row => (
                <MagicTableCellAggregationType
                  aggregationType={row.getValue<
                    UsageMetric['aggregationType']
                  >()}
                />
              )
            },
            {
              id: 'aggregationProperty',
              header: 'Event property',
              accessorKey: 'aggregationProperty',
              cell: row => {
                const aggregationProperty =
                  row.getValue<UsageMetric['aggregationProperty']>()
                return <MagicTableCell text={aggregationProperty || '-'} />
              }
            },
            {
              id: 'createdAt',
              header: 'Created',
              accessorKey: 'createdAt',
              cell: row => (
                <MagicTableCell
                  text={dateTimeWithFormat(
                    row.getValue<UsageMetric['createdAt']>(),
                    'd MMM yyyy'
                  )}
                />
              )
            }
          ]}
          kebabMenu={(model, props) => {
            const showKebabMenu =
              can('org.usage:read') || can('org.usage:update')

            if (!showKebabMenu) {
              return null
            }

            return (
              <SequenceMenu
                data-testid={`usageMetrics.${model.id}.menu`}
                buttonSize="small"
                alignment="bottom-right"
                closeMenuOnItemClick
                items={[
                  <ViewUsageMetric
                    key="view-usage-metric"
                    usageMetricId={model.id}
                  />,
                  <EditUsageMetric
                    key="edit-usage-metric"
                    usageMetricId={model.id}
                    isACustomMetric={model.aggregationType === 'CUSTOM'}
                  />
                ]}
                {...props}
              />
            )
          }}
          emptyStateUnfiltered={entityNamePlural => (
            <MagicTableEmptyState
              data-testid="usageMetrics.emptyState"
              entityNamePlural={entityNamePlural}
              imageSrc="/img/empty/gauge.png"
              learnMoreText="Create metrics to track usage events in real time and unlock usage-based billing"
              learnMoreHref="https://docs.sequencehq.com/metrics-events/examples"
              buttonNew={
                <ProtectedButton permission="org.usage:create">
                  <Button
                    variant="primary"
                    leadingIcon={<IconPlus color="inherit" />}
                    onClick={() => setShowCreateUsageMetricDrawer(true)}
                    data-testid="usageMetrics.emptyState.newMetric"
                  >
                    New usage metric
                  </Button>
                </ProtectedButton>
              }
            />
          )}
          emptyStateFiltered={(entityNamePlural, clearFilters) => (
            <MagicTableFilterEmptyState
              entityNamePlural={entityNamePlural}
              actionClearFilters={clearFilters}
              actionViewAll={() => navigate(usageMetricsPaths.list())}
              variant="TABLE"
            />
          )}
        />
      )}
    </CurrentUserId>
  )
}
